import { Modal } from 'rsuite';
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash.debounce';
import { BREAKPOINT_ENUM } from '../common/enums';

interface ResponsiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
}

const ResponsiveModal = ({
  isOpen,
  onClose,
  children,
  ariaLabel = '',
}: ResponsiveModalProps) => {
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= BREAKPOINT_ENUM.SMALL
  );
  const ariaId = useId();
  const triggerRef = useRef<Element | null>(null);

  // Capture the trigger element before RSuite moves focus into the modal.
  // useLayoutEffect runs before useEffect, so it fires before RSuite's internal
  // focus-into-modal logic (which uses useEffect).
  useLayoutEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
    }
  }, [isOpen]);

  // Restore focus to the trigger when the modal closes.
  useEffect(() => {
    if (!isOpen && triggerRef.current) {
      const trigger = triggerRef.current as HTMLElement;
      triggerRef.current = null;
      trigger?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleResize = debounce(() => {
      setIsMobile(window.innerWidth <= BREAKPOINT_ENUM.SMALL);
    }, 200);

    if (isOpen) {
      window.addEventListener('resize', handleResize);
    } else {
      window.removeEventListener('resize', handleResize);
    }

    // set to correct size on initial load
    handleResize();
  }, [isOpen]);

  return (
    <Modal
      aria-labelledby={`${ariaId}`}
      dialogClassName="responsive-modal no-padding-modal"
      style={{ animation: 'none' }} // turn off animation, otherwise you get a weird flicker when opening in full width
      size={isMobile === true ? 'full' : undefined}
      open={isOpen}
      onClose={onClose}
    >
      <label
        id={`${ariaId}`}
        className="sr-only"
      >
        {ariaLabel}-modal
      </label>
      {children}
    </Modal>
  );
};

export default ResponsiveModal;
